import {
  Button,
  Card,
  Column,
  Columns,
  Heading,
  Inline,
  breakpoints,
  vars,
  Text,
} from '@etg/wings';
import { LoadingDots, variants } from '@eti/components';
import { useTranslation } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import { WarningCircle } from '@phosphor-icons/react';
import type { Ticket, TicketPriceModalInfoProps } from '../model';
import { TicketPriceInfo } from '../ticket/common/TicketPriceInfo';

const buttonStyles = css`
  padding: 16px;
  width: 100%;
`;

const inlineStyles = css`
  color: ${vars.colors.signal.critical.light};
`;

const errorElementStyle = css`
  gap: 8px;
  margin-inline-start: 8px;
  opacity: 1;
`;

const columnStyles = (hasTicket: boolean) => css`
  align-items: center;
  display: flex;
  justify-content: ${hasTicket ? 'space-between' : 'center'};

  @media ${mediaQueries.medium.up} {
    justify-content: space-between;
  }
`;

interface ContinueBookingProps {
  hasError: boolean;
  isLoading: boolean;
  isMultipleTickets: boolean;
  isRyanAirTicketingOrMarketingCarrierPresent: boolean;
  isVI: boolean;
  onClick: () => void;
  ticket: Ticket | null;
  ticketPriceModalInfo: TicketPriceModalInfoProps;
  ticketVersion: number;
}

export const ContinueBooking = ({
  hasError,
  isLoading,
  isMultipleTickets,
  isRyanAirTicketingOrMarketingCarrierPresent,
  isVI,
  onClick,
  ticket,
  ticketPriceModalInfo,
  ticketVersion,
}: ContinueBookingProps) => {
  const { t } = useTranslation();

  return (
    <Card data-testid="continue-booking" padding={16}>
      <Columns collapseBelow={breakpoints._768} spacing={16}>
        <Column
          className={columnStyles(Boolean(ticket))}
          width={{ [breakpoints._0]: '6/6', [breakpoints._768]: '4/6' }}
        >
          {hasError && (
            <Inline alignY="center" className={errorElementStyle}>
              <WarningCircle
                aria-hidden
                color={vars.colors.signal.critical.light}
                data-testid="warning-icon"
                size={20}
              />
              <Text className={inlineStyles}>
                {t('Result.Page.SelectTicketType.Validation.Text')}
              </Text>
            </Inline>
          )}
          {!ticket && isMultipleTickets && !hasError && (
            <Inline alignY="center">{t('Result.Page.SelectTicketTypeToContinue.Text')}</Inline>
          )}
          {ticket && (
            <>
              <Heading level={4}>{ticket.ticketTitle}</Heading>
              <TicketPriceInfo
                isAtBottom
                isMultipleTickets={isMultipleTickets}
                isRyanAirTicketingOrMarketingCarrierPresent={
                  isRyanAirTicketingOrMarketingCarrierPresent
                }
                isVI={isVI}
                ticket={ticket}
                ticketPriceModalInfo={ticketPriceModalInfo}
                ticketVersion={ticketVersion}
              />
            </>
          )}
        </Column>
        <Column width={{ [breakpoints._0]: '6/6', [breakpoints._768]: '2/6' }}>
          <Button
            className={buttonStyles}
            data-testid="resultPage-continue-book-button"
            onClick={() => onClick()}
            size="small"
            variant={variants.PRIMARY}
          >
            {isLoading ? <LoadingDots /> : t('Result.Page.ContinueBooking.Text')}
          </Button>
        </Column>
      </Columns>
    </Card>
  );
};
